"use client"

import SelectImageButton from "@/(admin)/components/image/select-image-button"
import { FormSection, FormShell, StickyActionBar } from "@/(admin)/components/form"
import AccessDenied from "@/components/custom-access-denied"
import { CAREER_FORM } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { MediaData } from "@/types/types"
import { Button, Form, Input, Radio, Select } from "antd"
import { useState } from "react"
import { useUpsertCareerForm } from "../hooks/useUpsertCareerForm"
import Publish from "./PublishCareerForm"

const { TextArea } = Input

export default function CareerFormUpsertForm() {
  const [submitting, setSubmitting] = useState(false)
  const { hasPermission: checkPermission } = useCheckPermission()

  const {
    form,
    isEditMode,
    submitting: serviceSubmitting,
    domainOptions,
    careerOptions,
    isChange,
    onFinish,
    selectedCVMedia,
    setSelectedCVMedia,
    status,
    setStatus,
    handleBack,
    handleFormChange,
  } = useUpsertCareerForm()

  const requiredPermission = isEditMode ? "update" : "create"

  if (!checkPermission("career-form", requiredPermission)) {
    return <AccessDenied />
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const values = await form.validateFields()
      await onFinish(values)
    } catch (error) {
      console.error("VALIDATION_FAILED:", error)
    } finally {
      setSubmitting(false)
    }
  }

  const crumbs = [
    { title: t("Form tuyển dụng"), href: `/admin${CAREER_FORM.LIST.PATH}` },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <FormSection title={t("Thông tin ứng viên")} collapsible={false}>
      <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
        <Form.Item
          name="fullname"
          label={<span className="text-base font-medium">{t("Full Name")}</span>}
          rules={[{ required: true, message: t("Please enter full name") }]}
        >
          <Input placeholder={t("Enter full name")} />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="phone"
            label={<span className="text-base font-medium">{t("Phone")}</span>}
            rules={[
              { required: true, message: t("Vui lòng nhập số điện thoại") },
              {
                pattern: /^[+0-9\s]+$/,
                message: t("Số điện thoại chỉ được chứa các chữ số từ 0-9"),
              },
            ]}
          >
            <Input placeholder={t("Enter phone")} />
          </Form.Item>

          <Form.Item name="email" label={<span className="text-base font-medium">{t("Email")}</span>}>
            <Input placeholder={t("Enter email")} />
          </Form.Item>
        </div>

        <Form.Item
          name="cv"
          label={<span className="text-base font-medium">{t("CV")}</span>}
          rules={[{ required: true, message: t("Vui lòng tải lên CV") }]}
        >
          <SelectImageButton
            selectedMedia={selectedCVMedia}
            setSelectedMedia={(value: MediaData[]) => {
              setSelectedCVMedia(value)
              form.setFieldValue(
                "cv",
                Array.isArray(value) && value.length > 0 ? value.map(item => item.id) : undefined,
              )
              handleFormChange()
            }}
            selectMode="single"
            type="pdf"
          />
        </Form.Item>

        <Form.Item
          name="selfInstruction"
          label={<span className="text-base font-medium">{t("Self Introduction")}</span>}
        >
          <TextArea rows={4} placeholder={t("Enter self introduction")} />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item name="domain" label={<span className="text-base font-medium">{t("Domain")}</span>}>
            <Select
              placeholder={t("Select domain")}
              options={domainOptions}
              allowClear
              showSearch
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>

          <Form.Item name="career" label={<span className="text-base font-medium">{t("Career")}</span>}>
            <Select
              placeholder={t("Select career")}
              options={careerOptions}
              allowClear
              showSearch
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="resolved"
          label={<span className="text-base font-medium">{t("Resolved")}</span>}
          className="!mb-0"
        >
          <Radio.Group buttonStyle="solid">
            <Radio.Button value={false}>{t("False")}</Radio.Button>
            <Radio.Button value={true}>{t("True")}</Radio.Button>
          </Radio.Group>
        </Form.Item>
      </Form>
    </FormSection>
  )

  const sidebar = (
    <Publish
      status={status}
      setStatus={setStatus}
      onSubmit={handleSubmit}
      loading={submitting || serviceSubmitting}
      isEditMode={isEditMode}
    />
  )

  const actionBar = <StickyActionBar right={<Button onClick={handleBack}>{t("Cancel")}</Button>} />

  return (
    <FormShell
      title={isEditMode ? t("Edit Career Candidate") : t("Add Career Candidate")}
      crumbs={crumbs}
      main={main}
      sidebar={sidebar}
      actionBar={actionBar}
    />
  )
}